// ═══════════════════════════════════════════════════════════════════════════
// AI-callable safe-contour guard — LAYER-MANAGEMENT domain (T7, agent-bridge)
// ═══════════════════════════════════════════════════════════════════════════
//
// Mirrors `basePrimGuard.ts` / `viewSpaceGuard.ts`: maps every LAYER-domain AI
// action to the user-facing registry command it targets, then reuses the
// CC-2a generic `assertAiCallableCommand` to fail CLOSED when that target is
// not `aiCallable: true` (or is absent from the registry).
//
// All THREE actions target the single `LAYER` descriptor — including
// SET_ACTIVE_LAYER, whose translator emits `properties/apply` (an internal
// mechanic the descriptor does NOT list in `commandTypeLiterals` — the SAME
// class of "internal step, not the AI-callable surface" the
// `aiCallableGuard.ts` module comment documents for CREATE_DYNAMIC_BLOCK's
// `properties/apply`/`chprop/apply` steps). The guard is ACTION-level, not
// literal-level: the registry descriptor the ACTION targets is checked, never
// the internal EditorCommand literal the translator emits.

import {
  ALL_COMMAND_DESCRIPTORS,
  type CommandDescriptor,
  type CommandRegistryId,
} from "@dxf-ai-editor/command-registry";

import { assertAiCallableCommand } from "./aiCallableGuard";
import type { LayerAiCommand } from "./layerSchemas";

/**
 * Maps every LAYER-domain AI action to the registry command it targets. The
 * guard resolves this id and checks its `aiCallable` flag, so an action whose
 * target is ever flipped to `aiCallable: false` fails closed automatically.
 *
 * `Record<LayerAiCommand["action"], …>` forces exhaustiveness: adding a new
 * LAYER action without declaring its target command is a compile error.
 */
export const LAYER_AI_ACTION_TARGET_COMMAND: Record<
  LayerAiCommand["action"],
  CommandRegistryId
> = {
  CREATE_LAYER: "LAYER",
  RENAME_LAYER: "LAYER",
  SET_ACTIVE_LAYER: "LAYER",
};

/**
 * Guard a single LAYER-domain AI action: resolves its target registry
 * command and asserts AI-callability. Throws `NonAiCallableCommandError` when
 * the target is not AI-callable (fail-closed). Called by
 * `translateLayerCommand` before any EditorCommand is produced.
 */
export function assertAiCallableLayerAction(
  action: LayerAiCommand["action"],
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): void {
  assertAiCallableCommand(
    LAYER_AI_ACTION_TARGET_COMMAND[action],
    descriptors,
  );
}
